import React, { forwardRef, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Textarea } from "../components/ui/TextArea";
import { FormField } from "../components/FormField";
import { TagManagement } from "../components/TagManagement";
import { Pencil, ArrowLeft, PlayCircle, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Adapter } from '../types';
import { adapterFormHelpText, formatHelpText } from '../config/helptext-config';
import { useAuth } from '../contexts/AuthContext';
import { useUsersApi } from '../apis/users-apis';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/Select";
import { ConfirmationDialog } from '../components/ConfirmationDialog';

// View Mode Components
const ViewModeField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="mb-6">
    <h3 className="text-sm font-medium text-gray-500 mb-2">{label}</h3>
    {typeof value === 'string' ? (
      <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap border border-gray-200">
        {value || 'No content provided'}
      </div>
    ) : (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        {value}
      </div>
    )}
  </div>
);

export interface AdapterFormRef {
  validateForm: () => boolean;
}

// Parameter interface
interface Parameter {
  key: string;
  description: string;
  type: string;
  options?: string;
  example?: string;
}

// AdapterFormProps interface
interface AdapterFormProps {
  adapter: Adapter;
  originalAdapter?: Adapter | null;
  onAdapterChange: (adapter: Adapter) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isEditing: boolean;
  readOnly?: boolean;
  onEditClick?: () => void;
  onDiscardChanges?: () => void;
  hasUnsavedChanges?: boolean;
  setIsReadOnly?: (value: boolean) => void;
  tagSuggestions?: string[];
  existingAdapterNames?: string[];
  currentUserId: string;
}

interface User {
  userId: string;
  email: string;
  name?: string;
}

// View mode parameters component
const ViewModeParameters: React.FC<{ parameters: Parameter[] }> = ({ parameters = [] }) => {
  if (!Array.isArray(parameters) || parameters.length === 0) {
    return <div className="text-gray-500 italic">No parameters configured</div>;
  }

  return (
    <div className="space-y-4">
      {parameters.map((param, index) => (
        <div key={index} className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-start">
            <div className="flex-grow">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">{param.key}</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                  {param.type}
                </span>
              </div>
              <p className="text-gray-600 mt-1">{param.description}</p>
              {(param.options || param.example) && (
                <div className="mt-2 space-y-1 text-sm">
                  {param.options && (
                    <div className="text-gray-600">
                      <span className="font-medium">Options:</span> {param.options}
                    </div>
                  )}
                  {param.example && (
                    <div className="text-gray-600">
                      <span className="font-medium">Example:</span> {param.example}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const AdapterForm = forwardRef<AdapterFormRef, AdapterFormProps>((props, ref) => {
  const {
    adapter,
    originalAdapter,
    onAdapterChange,
    onSubmit,
    onCancel,
    isEditing = false,
    readOnly = false,
    onEditClick,
    existingAdapterNames = [],
  } = props;

  const navigate = useNavigate();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isAuthFieldTouched, setIsAuthFieldTouched] = useState(false);
  const { user } = useAuth();
  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false);
  const { getUsers } = useUsersApi();
  const [isAddingParameter, setIsAddingParameter] = useState(false);
  const [newParamKey, setNewParamKey] = useState("");
  const [newParamDesc, setNewParamDesc] = useState("");
  const [newParamType, setNewParamType] = useState("str");
  const [newParamOptions, setNewParamOptions] = useState("");
  const [newParamExample, setNewParamExample] = useState("");
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Parameter editing state
  const [editingParam, setEditingParam] = useState<number | null>(null);
  const [editParamKey, setEditParamKey] = useState("");
  const [editParamDesc, setEditParamDesc] = useState("");
  const [editParamType, setEditParamType] = useState("str");
  const [editParamOptions, setEditParamOptions] = useState("");
  const [editParamExample, setEditParamExample] = useState("");
  const [editErrors, setEditErrors] = useState<{
    key?: string;
    description?: string;
  }>({});

  // Parameter deletion state
  const [parameterToDelete, setParameterToDelete] = useState<number | null>(null);

  // Base URL for API endpoint
  // const baseUrl = `https://${process.env.REACT_APP_ENVIRONMENT}.agenticgenie.click/external-adapter/`;
  const baseUrl = `${process.env.REACT_APP_API_URL}/external-adapter/`;

  const apiEndpointAuthMessage = (
    <>
      This API endpoint requires a Key to be passed in the 'X-API-Key' header for authentication. This key can be found in the AWS Secrets Manager under <strong>agenticgenie/{process.env.REACT_APP_ENVIRONMENT}/api-key</strong>.
    </>
  );

  // Ensure adapter parameters are initialized
  useEffect(() => {
    if (!adapter.adapterParameters?.parameters) {
      onAdapterChange({
        ...adapter,
        adapterParameters: {
          ...adapter.adapterParameters,
          parameters: [],
          testMode: true
        }
      });
    }

    // Always set access control to "Owner Only" for adapters
    if (!adapter.accessControl || 
        adapter.accessControl.editAccessType !== 'Owner Only' || 
        adapter.accessControl.viewAccessType !== 'Owner Only') {
      onAdapterChange({
        ...adapter,
        accessControl: {
          editAccessType: 'Owner Only',
          editAllowedUsers: [],
          viewAccessType: 'Owner Only',
          viewAllowedUsers: []
        }
      });
    }
  }, [adapter, onAdapterChange]);

  // Check if current user is admin
  useEffect(() => {
    const fetchAdminStatus = async () => {
      if (user?.sub) {
        try {
          const usersData = await getUsers();
          const userData = usersData.find((u) => u.userId === user.sub);
          setIsCurrentUserAdmin(userData?.isAdmin || false);
        } catch (error) {
          console.error('Error fetching user data:', error);
          setIsCurrentUserAdmin(false);
        }
      }
    };
  
    fetchAdminStatus();
  }, [user?.sub]);

  const handleInputTouch = (fieldName: string) => {
    setTouchedFields(prev => new Set(prev).add(fieldName));
  };

  // State for owner details
  const [ownerDetails, setOwnerDetails] = useState({ email: '', name: '' });
  
  // Effect to fetch owner details
  useEffect(() => {
    const fetchOwnerDetails = async () => {
      try {
        const usersData = await getUsers();
        const owner = usersData.find((user: User) => user.userId === adapter.ownerId);
        if (owner) {
          setOwnerDetails({
            email: owner.email,
            name: owner.email
          });
        }
      } catch (error) {
        console.error('Error fetching owner details:', error);
      }
    };
    
    if (readOnly) {
      fetchOwnerDetails();
    }
  }, [adapter.ownerId, readOnly]);
  
  // View mode rendering
  const renderViewMode = () => {
    return (
      <Card className="max-w-3xl mx-auto">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-2xl">{adapter.adapterName}</CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/studio/admin/adapters/test/${adapter.adapterId}`)}
              className="border-green-500 text-green-500 hover:bg-green-50 hover:text-green-600"
            >
              <PlayCircle className="mr-2 h-4 w-4" />
              Test
            </Button>
            {isCurrentUserAdmin && (
              <Button
                variant="outline"
                onClick={onEditClick}
                className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
            )}
            <Button
              variant="outline"
              onClick={onCancel}
              className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Adapters
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <ViewModeField 
            label="Description" 
            value={adapter.adapterDescription} 
          />
          
          <ViewModeField 
            label="API Endpoint" 
            value={adapter.adapterUrlEndpoint} 
          />
          <p className="text-sm text-blue-600 font-medium">
          {apiEndpointAuthMessage}
          </p>
          
          <ViewModeField 
            label="Target URL" 
            value={adapter.adapterTargetUrl} 
          />
          
          <ViewModeField 
            label="Authentication" 
            value="••••••••" 
          />
          
          <ViewModeField 
            label="Tags" 
            value={
              <TagManagement
                tags={adapter.adapterTags || []}
                onChange={() => {}}
                readOnly={true}
                suggestions={props.tagSuggestions}
              />
            }
          />

          <ViewModeField
            label="Input Parameters"
            value={
              <Card className="bg-gray-50 border-none shadow-none p-4">
                <ViewModeParameters parameters={adapter.adapterParameters?.parameters ?? []} />
              </Card>
            }
          />

          <ViewModeField
            label="Access Control"
            value={
              <div className="flex items-center space-x-2 text-gray-700">
                <Info className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="font-medium">Administrator Only</p>
                  <p className="text-sm text-gray-600">
                    This adapter is managed by administrators only. Owner: {ownerDetails.email || 'Administrator'}
                  </p>
                </div>
              </div>
            }
          />
        </CardContent>
      </Card>
    );
  };

  const renderEditMode = () => {
    return (
      <Card className="max-w-3xl mx-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-2xl">{isEditing ? 'Edit Adapter' : 'Add New Adapter'}</CardTitle>
          <Button
            variant="outline"
            onClick={onCancel}
            className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Adapters
          </Button>
        </CardHeader>

        <CardContent className="space-y-6">
          <FormField
            label="Adapter Name"
            required
            helpText={formatHelpText(adapterFormHelpText.adapterName)}
            error={errors.adapterName}
          >
            <Input
              value={adapter.adapterName}
              onChange={(e) => {
                const newName = e.target.value;
                // Update the adapter state with both the new name and the auto-generated endpoint
                onAdapterChange({ 
                  ...adapter, 
                  adapterName: newName,
                  adapterUrlEndpoint: baseUrl + newName.toLowerCase()
                });
              }}
              onBlur={(e) => {
                // Ensure the API endpoint is updated on blur as well
                const newName = e.target.value;
                onAdapterChange({ 
                  ...adapter, 
                  adapterName: newName,
                  adapterUrlEndpoint: baseUrl + newName.toLowerCase()
                });
              }}
              placeholder="Enter adapter name"
              className={errors.adapterName ? 'border-red-500' : ''}
            />
          </FormField>

          <FormField
            label="Adapter Description"
            required
            helpText={formatHelpText(adapterFormHelpText.adapterDescription)}
            error={errors.adapterDescription}
          >
            <Textarea
              value={adapter.adapterDescription}
              onChange={(e) => onAdapterChange({ ...adapter, adapterDescription: e.target.value })}
              placeholder="Enter adapter description"
              rows={3}
              className={errors.adapterDescription ? 'border-red-500' : ''}
            />
          </FormField>

          <FormField
            label="API Endpoint"
            required
            helpText={formatHelpText(adapterFormHelpText.adapterUrlEndpoint)}
            error={errors.adapterUrlEndpoint}
          >
            <div className="flex flex-col space-y-2">
              <div className="relative">
                <Input
                  value={adapter.adapterUrlEndpoint}
                  readOnly
                  className={`${errors.adapterUrlEndpoint ? 'border-red-500' : ''} bg-gray-100`}
                />
              </div>
              <p className="text-sm text-gray-500">
                This field is automatically generated from the Adapter Name.
              </p>
              <p className="text-sm text-blue-600 mt-2 font-medium">
                {apiEndpointAuthMessage}
              </p>
            </div>
          </FormField>

          <FormField
            label="Target URL"
            required
            helpText="Enter the target HTTP(S) URL for this adapter"
            error={errors.adapterTargetUrl}
          >
            <Input
              value={adapter.adapterTargetUrl}
              onChange={(e) => onAdapterChange({ ...adapter, adapterTargetUrl: e.target.value })}
              placeholder="https://api.example.com"
              className={errors.adapterTargetUrl ? 'border-red-500' : ''}
            />
          </FormField>

          <FormField
            label="Authentication"
            required
            helpText={formatHelpText(adapterFormHelpText.adapterAuthentication)}
            error={errors.adapterAuthentication}
          >
            <Input
              type="password"
              value={adapter.adapterAuthentication}
              onChange={(e) => onAdapterChange({ ...adapter, adapterAuthentication: e.target.value })}
              placeholder="••••••••"
              className={errors.adapterAuthentication ? 'border-red-500' : ''}
              onFocus={() => {
                if (!isAuthFieldTouched) {
                  setIsAuthFieldTouched(true);
                  onAdapterChange({ ...adapter, adapterAuthentication: '' });
                }
              }}
            />
          </FormField>

          <FormField
            label="Tags"
            helpText={formatHelpText(adapterFormHelpText.adapterTags)}
          >
            <TagManagement
              tags={adapter.adapterTags || []}
              onChange={(newTags) => onAdapterChange({ ...adapter, adapterTags: newTags })}
              readOnly={false}
              suggestions={props.tagSuggestions}
            />
          </FormField>

          <FormField
            label="Input Parameters"
            helpText="Define the input parameters for this adapter"
          >
            {!isAddingParameter ? (
              <div className="flex justify-start">
                <Button
                  type="button"
                  onClick={() => setIsAddingParameter(true)}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                >
                  Add Input Parameter
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex space-x-2">
                  <div className="flex-1">
                    <div className="mb-1 text-sm font-medium text-gray-700">
                      Parameter Key <span className="text-red-500">*</span>
                    </div>
                    <Input
                      value={newParamKey}
                      onChange={(e) => setNewParamKey(e.target.value)}
                      onBlur={() => handleInputTouch('paramKey')}
                      placeholder="Enter parameter key"
                      className={`${touchedFields.has('paramKey') && !newParamKey ? 'border-red-300' : ''}`}
                      required
                    />
                    {touchedFields.has('paramKey') && !newParamKey && 
                      <p className="mt-1 text-sm text-red-500">Parameter key is required</p>
                    }
                  </div>
                  
                  <div className="flex-1">
                    <div className="mb-1 text-sm font-medium text-gray-700">
                      Description <span className="text-red-500">*</span>
                    </div>
                    <Input
                      value={newParamDesc}
                      onChange={(e) => setNewParamDesc(e.target.value)}
                      onBlur={() => handleInputTouch('paramDesc')}
                      placeholder="Enter description"
                      className={`${touchedFields.has('paramDesc') && !newParamDesc ? 'border-red-300' : ''}`}
                      required
                    />
                    {touchedFields.has('paramDesc') && !newParamDesc && 
                      <p className="mt-1 text-sm text-red-500">Description is required</p>
                    }
                  </div>

                  <div>
                    <div className="mb-1 text-sm font-medium text-gray-700">
                      Type <span className="text-red-500">*</span>
                    </div>
                    <Select value={newParamType} onValueChange={setNewParamType}>
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="int">Integer</SelectItem>
                        <SelectItem value="float">Float</SelectItem>
                        <SelectItem value="complex">Complex</SelectItem>
                        <SelectItem value="bool">Boolean</SelectItem>
                        <SelectItem value="str">String</SelectItem>
                        <SelectItem value="list">List</SelectItem>
                        <SelectItem value="tuple">Tuple</SelectItem>
                        <SelectItem value="dict">Dictionary</SelectItem>
                        <SelectItem value="set">Set</SelectItem>
                        <SelectItem value="frozenset">Frozen Set</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <div className="flex-1">
                    <div className="mb-1 text-sm font-medium text-gray-700 flex items-center">
                      Parameter Options
                      <span className="ml-1 text-gray-500 text-sm">(Optional)</span>
                    </div>
                    <Input
                      value={newParamOptions}
                      onChange={(e) => setNewParamOptions(e.target.value)}
                      placeholder="Add parameter options if any"
                    />
                  </div>
                  
                  <div className="flex-1">
                    <div className="mb-1 text-sm font-medium text-gray-700 flex items-center">
                      Parameter Example
                      <span className="ml-1 text-gray-500 text-sm">(Optional)</span>
                    </div>
                    <Input
                      value={newParamExample}
                      onChange={(e) => setNewParamExample(e.target.value)}
                      placeholder="Add usage example if any"
                    />
                  </div>

                  <div className="flex items-end space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsAddingParameter(false);
                        setNewParamKey("");
                        setNewParamDesc("");
                        setNewParamType("str");
                        setNewParamOptions("");
                        setNewParamExample("");
                        setTouchedFields(new Set());
                      }}
                      className="text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        if (newParamKey && newParamDesc) {
                          onAdapterChange({
                            ...adapter,
                            adapterParameters: {
                              ...adapter.adapterParameters,
                              parameters: [
                                ...adapter.adapterParameters.parameters,
                                {
                                  key: newParamKey,
                                  description: newParamDesc,
                                  type: newParamType,
                                  ...(newParamOptions && { options: newParamOptions }),
                                  ...(newParamExample && { example: newParamExample })
                                }
                              ]
                            }
                          });
                          // Reset form
                          setNewParamKey("");
                          setNewParamDesc("");
                          setNewParamOptions("");
                          setNewParamExample("");
                          setNewParamType("str");
                          setTouchedFields(new Set());
                          setIsAddingParameter(false);
                        }
                      }}
                      className="bg-blue-500 hover:bg-blue-600 text-white"
                      disabled={!newParamKey || !newParamDesc}
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Parameters List */}
            <div className="space-y-2 mt-4">
              {adapter.adapterParameters.parameters.map((param, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-start">
                    <div className="flex-grow">
                      <span className="font-medium">{param.key}</span>
                      {editingParam === index ? (
                        <div className="space-y-2 mt-2">
                          <div className="flex items-center space-x-2">
                            <div className="flex-1">
                              <Input
                                value={editParamKey}
                                onChange={(e) => {
                                  setEditParamKey(e.target.value);
                                  if (editErrors.key) {
                                    setEditErrors(prev => ({ ...prev, key: undefined }));
                                  }
                                }}
                                placeholder="Parameter Key"
                                className={`${editErrors.key ? 'border-red-300' : ''}`}
                              />
                              {editErrors.key && (
                                <p className="mt-1 text-sm text-red-500">{editErrors.key}</p>
                              )}
                            </div>
                            
                            <div className="flex-1">
                              <Input
                                value={editParamDesc}
                                onChange={(e) => {
                                  setEditParamDesc(e.target.value);
                                  if (editErrors.description) {
                                    setEditErrors(prev => ({ ...prev, description: undefined }));
                                  }
                                }}
                                placeholder="Description"
                                className={`${editErrors.description ? 'border-red-300' : ''}`}
                              />
                              {editErrors.description && (
                                <p className="mt-1 text-sm text-red-500">{editErrors.description}</p>
                              )}
                            </div>

                            <Select value={editParamType} onValueChange={setEditParamType}>
                              <SelectTrigger className="w-32">
                                <SelectValue placeholder="Type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="int">Integer</SelectItem>
                                <SelectItem value="float">Float</SelectItem>
                                <SelectItem value="complex">Complex</SelectItem>
                                <SelectItem value="bool">Boolean</SelectItem>
                                <SelectItem value="str">String</SelectItem>
                                <SelectItem value="list">List</SelectItem>
                                <SelectItem value="tuple">Tuple</SelectItem>
                                <SelectItem value="dict">Dictionary</SelectItem>
                                <SelectItem value="set">Set</SelectItem>
                                <SelectItem value="frozenset">Frozen Set</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="flex space-x-2">
                            <Input
                              value={editParamOptions}
                              onChange={(e) => setEditParamOptions(e.target.value)}
                              placeholder="Parameter Options (Optional)"
                              className="flex-1"
                            />
                            <Input
                              value={editParamExample}
                              onChange={(e) => setEditParamExample(e.target.value)}
                              placeholder="Parameter Example (Optional)"
                              className="flex-1"
                            />
                          </div>

                          <div className="flex justify-end space-x-2 mt-2">
                            <Button
                              variant="outline"
                              onClick={() => {
                                setEditingParam(null);
                                setEditErrors({});
                              }}
                              className="text-gray-600 hover:text-gray-800"
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => handleParameterEdit(index)}
                              className="text-green-600 hover:text-green-800"
                            >
                              Save
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <span className="ml-2 text-gray-600">- {param.description}</span>
                          <span className="ml-2 text-gray-500">({param.type})</span>
                          {(param.options || param.example) && (
                            <div className="text-sm text-gray-500 mt-1">
                              {param.options && <span>Options: {param.options}</span>}
                              {param.options && param.example && <span> • </span>}
                              {param.example && <span>Example: {param.example}</span>}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {editingParam !== index && (
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingParam(index);
                            setEditParamKey(param.key);
                            setEditParamDesc(param.description);
                            setEditParamType(param.type);
                            setEditParamOptions(param.options || "");
                            setEditParamExample(param.example || "");
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleParameterRemove(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </FormField>

          {/* Access Control Note */}
          <div className="border p-4 rounded-md bg-blue-50 flex items-start space-x-3">
            <Info className="h-5 w-5 text-blue-500 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-blue-800 mb-1">Administrator Access Only</h3>
              <p className="text-sm text-blue-700">
                Adapters are managed by administrators only. Access control is automatically set to "Owner Only" and cannot be modified.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={onSubmit} className="bg-blue-500 hover:bg-blue-600 text-white">
              {isEditing ? 'Save Changes' : 'Create Adapter'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const validateParameter = (key: string, description: string) => {
    const errors: { key?: string; description?: string } = {};
    
    if (!key.trim()) {
      errors.key = 'Parameter key is required';
    }
    if (!description.trim()) {
      errors.description = 'Description is required';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  };

  const handleParameterEdit = (index: number) => {
    const validation = validateParameter(editParamKey, editParamDesc);
    
    if (!validation.isValid) {
      setEditErrors(validation.errors);
      return;
    }

    onAdapterChange({
      ...adapter,
      adapterParameters: {
        ...adapter.adapterParameters,
        parameters: adapter.adapterParameters.parameters.map((param, i) => 
          i === index ? {
            key: editParamKey,
            description: editParamDesc,
            type: editParamType,
            ...(editParamOptions && { options: editParamOptions }),
            ...(editParamExample && { example: editParamExample })
          } : param
        )
      }
    });
    setEditingParam(null);
    setEditErrors({});
  };

  const handleParameterRemove = (index: number) => {
    setParameterToDelete(index);
  };
  
  const confirmParameterRemove = () => {
    if (parameterToDelete !== null) {
      onAdapterChange({
        ...adapter,
        adapterParameters: {
          ...adapter.adapterParameters,
          parameters: adapter.adapterParameters.parameters.filter((_, i) => i !== parameterToDelete)
        }
      });
      setParameterToDelete(null);
    }
  };

  // Validate form function exposed through the ref
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!adapter.adapterName?.trim()) {
      newErrors.adapterName = 'Adapter name is required';
    } else if (!/^[a-z0-9-]+$/.test(adapter.adapterName)) {
      newErrors.adapterName = 'Only lowercase letters, numbers, and hyphens are allowed';
    }


    if (!adapter.adapterDescription?.trim()) {
      newErrors.adapterDescription = 'Description is required'; 
    }

    if (!adapter.adapterUrlEndpoint?.trim()) {
      newErrors.adapterUrlEndpoint = 'API endpoint is required';
    }

    if (!adapter.adapterTargetUrl?.trim()) {
      newErrors.adapterTargetUrl = 'Target URL is required';
    } else {
      try {
        new URL(adapter.adapterTargetUrl); // This will throw if URL is invalid
      } catch (e) {
        newErrors.adapterTargetUrl = 'Please enter a valid HTTP(S) URL';
      }
    }

    if (!adapter.adapterAuthentication?.trim()) {
      // Only show error if this is a new adapter OR if the auth field has been touched
      if (!isEditing || isAuthFieldTouched) {
        newErrors.adapterAuthentication = 'Authentication is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Expose the validation method through the ref
  useEffect(() => {
    if (ref) {
      (ref as any).current = {
        validateForm
      };
    }
  }, [adapter]);

  return (
    <div className="container mx-auto p-4">
      {readOnly ? renderViewMode() : renderEditMode()}
      <ConfirmationDialog
        open={parameterToDelete !== null}
        onOpenChange={(open) => !open && setParameterToDelete(null)}
        onConfirm={confirmParameterRemove}
        title="Remove Parameter"
        description="Are you sure you want to remove this parameter? This action cannot be undone."
        confirmLabel="Remove"
        cancelLabel="Cancel"
      />
    </div>
  );
});

export default AdapterForm;